/**
 * Benvel Section Rendering API helpers — augments Horizon's `sectionRenderer`
 * with focus restore, scroll-restoration policy, Benvel WC re-mount flag,
 * single silent retry, and `benvel:section-rendered` / `benvel:network-error`
 * event dispatch. See docs/section-renderer-pre-flight.md + docs/events.md.
 * Anchors: D2 (architecture.md:273), Substrate-vs-Additions (architecture.md:904),
 * event namespace (project-context §24).
 */

import {
  sectionRenderer,
  morphSection,
  normalizeSectionId,
  buildSectionSelector,
} from "@theme/section-renderer";

const RETRY_DELAY_MS = 1500;
const latestSwapTokens = new Map();

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Returns a querySelector key, the literal `'off'`, or `null`. */
function captureFocusKey(active) {
  if (!(active instanceof Element) || active === document.body) return null;
  const explicit =
    active.getAttribute && active.getAttribute("data-restore-focus");
  if (explicit === "off") return "off";
  if (explicit) return explicit;
  if (active.id) return `#${CSS.escape(active.id)}`;

  const sectionRoot = active.closest(".shopify-section");
  if (!sectionRoot) return null;

  const segments = [];
  let node = active;
  for (
    let depth = 0;
    depth < 5 && node && node.nodeType === Node.ELEMENT_NODE;
    depth += 1
  ) {
    const parent = node.parentElement;
    if (!parent || parent === sectionRoot) {
      const tag = node.tagName.toLowerCase();
      const siblings = Array.from(sectionRoot.children).filter(
        (child) => child.tagName === node.tagName,
      );
      segments.unshift(`${tag}:nth-of-type(${siblings.indexOf(node) + 1})`);
      break;
    }
    const tag = node.tagName.toLowerCase();
    const siblings = Array.from(parent.children).filter(
      (c) => c.tagName === node.tagName,
    );
    segments.unshift(`${tag}:nth-of-type(${siblings.indexOf(node) + 1})`);
    node = parent;
  }
  return segments.length ? segments.join(" > ") : null;
}

function restoreFocusByKey(root, key) {
  if (!root || key === null || key === "off") return;
  let target = null;
  try {
    target = root.querySelector(key);
  } catch {
    target = null;
  }
  if (target && typeof target.focus === "function") {
    target.focus({ preventScroll: true });
  }
}

/**
 * Sets `_mounted = false` on every `benvel-*` instance under `root`. Contract:
 * Benvel WCs that need re-init after an SRA swap MUST check
 * `this._mounted === false` in their re-init path. Horizon's `morph()` does
 * NOT re-fire `connectedCallback`; this flag is the rehydration signal.
 */
function markBenvelComponentsForRemount(root) {
  if (!root) return;
  for (const el of root.querySelectorAll("*")) {
    if (el.tagName && el.tagName.startsWith("BENVEL-")) el._mounted = false;
  }
}

function applyScrollPolicy(section, policy, previousScrollY) {
  if (policy === "preserve") {
    window.scrollTo({ top: previousScrollY, behavior: "auto" });
    return;
  }
  if (policy === "into-view" && section) {
    section.scrollIntoView({
      block: "start",
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  }
}

function dispatchRendered(sectionId, source) {
  document.dispatchEvent(
    new CustomEvent("benvel:section-rendered", {
      bubbles: true,
      detail: { sectionId, source },
    }),
  );
}

function errorDetail(error) {
  return {
    name: error?.name ?? "Error",
    message: error?.message ?? String(error),
  };
}

function requireSource(source, helperName) {
  if (typeof source !== "string" || source.trim() === "") {
    throw new Error(`${helperName}: options.source is required`);
  }
  return source;
}

function registerSwapToken(sectionId) {
  const token = Symbol(sectionId);
  latestSwapTokens.set(sectionId, token);
  return token;
}

function isLatestSwap(sectionId, token) {
  return latestSwapTokens.get(sectionId) === token;
}

function clearSwapToken(sectionId, token) {
  if (isLatestSwap(sectionId, token)) {
    latestSwapTokens.delete(sectionId);
  }
}

function normalizeMorphMarkup(html) {
  if (typeof html === "string") return html;
  if (html instanceof Document) return html.documentElement.outerHTML;
  if (html instanceof Element) return html.outerHTML;
  throw new Error(
    "morphSectionWithBenvelLifecycle: html must be a string, Document, or Element",
  );
}

function dispatchNetworkError(sectionId, source, error) {
  document.dispatchEvent(
    new CustomEvent("benvel:network-error", {
      bubbles: true,
      detail: { sectionId, source, error: errorDetail(error) },
    }),
  );
}

function runPostMorph(normalizedId, focusKey, scrollY, scrollPolicy, source) {
  const section = document.getElementById(buildSectionSelector(normalizedId));
  markBenvelComponentsForRemount(section);
  restoreFocusByKey(section, focusKey);
  applyScrollPolicy(section, scrollPolicy, scrollY);
  dispatchRendered(normalizedId, source);
}

/**
 * Canonical Benvel partial-render helper. Wraps `sectionRenderer.renderSection`
 * with the Benvel lifecycle.
 *
 * @param {string} sectionId - With or without `shopify-section-` prefix.
 * @param {Object} options
 * @param {URL|string} [options.url] - Defaults to current location.
 * @param {string} options.source - Caller tag for the dispatched event detail.
 * @param {'full'|'hydration'} [options.mode='full']
 * @param {'preserve'|'into-view'|'none'} [options.restoreScrollPolicy='preserve']
 * @returns {Promise<{ok:boolean,sectionId:string,aborted?:boolean,error?:{name:string,message:string}}>}
 *
 * @example
 *   await swapSection('main-collection', { source: 'plp-filter', restoreScrollPolicy: 'into-view' });
 */
export async function swapSection(sectionId, options = {}) {
  const url = options.url;
  const source = requireSource(options.source, "swapSection");
  const mode = options.mode ?? "full";
  const scrollPolicy = options.restoreScrollPolicy ?? "preserve";
  const normalizedId = normalizeSectionId(sectionId);

  if (!document.getElementById(buildSectionSelector(normalizedId))) {
    throw new Error(`swapSection: section ${sectionId} not in DOM`);
  }

  const swapToken = registerSwapToken(normalizedId);
  const focusKey = captureFocusKey(document.activeElement);
  const scrollY = scrollPolicy === "preserve" ? window.scrollY : 0;

  const renderOptions = { mode };
  if (url !== undefined) renderOptions.url = url;

  try {
    try {
      await sectionRenderer.renderSection(normalizedId, renderOptions);
    } catch (error) {
      if (error && error.name === "AbortError") {
        return { ok: false, sectionId: normalizedId, aborted: true };
      }
      try {
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
        if (!isLatestSwap(normalizedId, swapToken)) {
          return { ok: false, sectionId: normalizedId, aborted: true };
        }
        await sectionRenderer.renderSection(normalizedId, renderOptions);
      } catch (retryError) {
        if (retryError && retryError.name === "AbortError") {
          return { ok: false, sectionId: normalizedId, aborted: true };
        }
        if (!isLatestSwap(normalizedId, swapToken)) {
          return { ok: false, sectionId: normalizedId, aborted: true };
        }
        dispatchNetworkError(normalizedId, source, retryError);
        return {
          ok: false,
          sectionId: normalizedId,
          error: errorDetail(retryError),
        };
      }
    }

    if (!isLatestSwap(normalizedId, swapToken)) {
      return { ok: false, sectionId: normalizedId, aborted: true };
    }

    runPostMorph(normalizedId, focusKey, scrollY, scrollPolicy, source);
    return { ok: true, sectionId: normalizedId };
  } finally {
    clearSwapToken(normalizedId, swapToken);
  }
}

/**
 * Variant for callers that already have HTML (e.g. `VariantUpdateEvent.detail.data.html`).
 * No retry, no `benvel:network-error`; if `morphSection` throws, it propagates.
 *
 * @param {string} sectionId
 * @param {string|Document|Element} html
 * @param {Object} options
 * @param {string} options.source
 * @param {'full'|'hydration'} [options.mode='full']
 * @param {'preserve'|'into-view'|'none'} [options.restoreScrollPolicy='preserve']
 * @param {boolean} [options.injectStylesheet=false]
 * @returns {Promise<{ok:true,sectionId:string}>}
 */
export async function morphSectionWithBenvelLifecycle(
  sectionId,
  html,
  options = {},
) {
  const source = requireSource(
    options.source,
    "morphSectionWithBenvelLifecycle",
  );
  const mode = options.mode ?? "full";
  const scrollPolicy = options.restoreScrollPolicy ?? "preserve";
  const injectStylesheet = options.injectStylesheet ?? false;
  const normalizedId = normalizeSectionId(sectionId);

  const focusKey = captureFocusKey(document.activeElement);
  const scrollY = scrollPolicy === "preserve" ? window.scrollY : 0;

  await morphSection(normalizedId, normalizeMorphMarkup(html), {
    mode,
    injectStylesheet,
  });

  runPostMorph(normalizedId, focusKey, scrollY, scrollPolicy, source);
  return { ok: true, sectionId: normalizedId };
}
